import { SHAPES, shapeFor, type ShapeName } from '../components/ui/TypologyShape';
import type { AnalysisType, ComplianceAnalysisResult } from '../../shared/types';

/* ════════════════════════════════════════════════════════════════════
   CHARTE « CREAM & INK » POUR LES EXPORTS
   ────────────────────────────────────────────────────────────────────
   Les rapports exportés (PDF, email) sont rendus dans un document
   ISOLÉ : pas de Tailwind, pas de variables CSS, pas de classes. Tout
   doit donc être écrit en dur, en style inline. Ce module est la
   traduction littérale des tokens de `client/index.css` — c'est lui
   qu'on met à jour si la charte bouge, pas les deux générateurs.

   Contrainte technique qui explique la forme : html2canvas 1.x ne sait
   pas lire `oklch()` — aucune couleur Tailwind 4 ne doit donc fuiter
   ici — et les clients mail ignorent la moitié du CSS. D'où des aplats
   hexadécimaux, des tables, et zéro dépendance externe.
   ════════════════════════════════════════════════════════════════════ */

/** Neutres chauds — le crème et l'encre de la charte. */
export const C = {
  cream: '#efe7da',      // fond de page, derrière les cartes
  app: '#fbf6ee',        // surface applicative — carte « écran »
  card: '#f2ece1',       // carte neutre
  ink: '#101010',        // encre : cadres, titres, texte fort
  line: '#e7e0d3',       // filet
  lineSoft: '#f4eee4',
  title: '#161514',
  body: '#413d36',
  muted: '#5d584f',
  soft: '#7f7a6e',
  faint: '#a8a093',
} as const;

/** Une teinte du nuancier : l'aplat, ses deux dilutions, et le ton
 *  profond — le seul assez sombre pour porter du texte sur la dilution. */
export interface Tone { base: string; soft: string; wash: string; deep: string }

export const TONES: Record<string, Tone> = {
  sun:     { base: '#f5dd6d', soft: '#fbefc0', wash: '#fdf8e4', deep: '#8d7118' },
  rose:    { base: '#f5bcdb', soft: '#fbe2f0', wash: '#fdf1f8', deep: '#93406e' },
  sky:     { base: '#b7cdf0', soft: '#e3ecfa', wash: '#f2f6fd', deep: '#3a5280' },
  khaki:   { base: '#c4d3a4', soft: '#e8eed6', wash: '#f4f7ec', deep: '#566635' },
  clay:    { base: '#efac80', soft: '#fae2d1', wash: '#fdf3ec', deep: '#8a4922' },
  red:     { base: '#ec9a91', soft: '#fadedb', wash: '#fdf1f0', deep: '#8e3230' },
  neutral: { base: '#e7e0d3', soft: '#f4eee4', wash: '#fbf7f0', deep: '#413d36' },
};

/** Teinte par typologie — même répartition que `client/utils/tints.ts`. */
const TONE_BY_TYPE: Record<string, keyof typeof TONES> = {
  general_question: 'sky',
  brand_name: 'sun',
  slogan: 'rose',
  concept: 'khaki',
  label: 'clay',
  campaign: 'sun',
  social_post: 'rose',
  event_activation: 'sky',
  creative_brief: 'khaki',
};

export function toneForType(type: AnalysisType | string | null | undefined): Tone {
  return TONES[(type && TONE_BY_TYPE[type]) || 'neutral'];
}

/* ── Verdict ─────────────────────────────────────────────────────────
   Le libellé court est celui du badge de l'application ; la phrase
   longue est celle du site — « ce que ça veut dire pour vous ». */

export interface VerdictStyle { key: string; label: string; lede: string; tone: Tone }

const VERDICTS: Record<string, VerdictStyle> = {
  CONFORME:      { key: 'CONFORME',     label: 'Conforme',     lede: 'Risque de refus très faible',   tone: TONES.khaki },
  VIGILANCE:     { key: 'VIGILANCE',    label: 'Vigilance',    lede: 'Zones grises à clarifier',      tone: TONES.sun },
  RISQUE_ELEVE:  { key: 'RISQUE_ELEVE', label: 'Risque élevé', lede: 'Risque de refus de visa élevé', tone: TONES.clay },
  RISQUE_MODERE: { key: 'RISQUE_ELEVE', label: 'Risque élevé', lede: 'Risque de refus de visa élevé', tone: TONES.clay },
  NON_CONFORME:  { key: 'NON_CONFORME', label: 'Non conforme', lede: 'Refus de visa probable',        tone: TONES.red },
};

/** Verdict rendu par l'arbitre. On fait confiance au champ `verdict` ;
 *  les seuils ne servent que s'il manque (analyses d'historique anciennes). */
export function verdictStyle(result: Pick<ComplianceAnalysisResult, 'score' | 'verdict'>): VerdictStyle {
  const fromField = result.verdict && VERDICTS[result.verdict];
  if (fromField) return fromField;
  if (result.score >= 80) return VERDICTS.CONFORME;
  if (result.score >= 60) return VERDICTS.VIGILANCE;
  if (result.score >= 40) return VERDICTS.RISQUE_ELEVE;
  return VERDICTS.NON_CONFORME;
}

/* ── Typographie ─────────────────────────────────────────────────────
   Acorn n'existe qu'en Regular : ne jamais lui demander de graisse, le
   navigateur la fabriquerait — un faux gras se voit sur un titre. */
export const FONT_SANS = "'Outfit',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif";
export const FONT_DISPLAY = "'Acorn','Outfit',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif";

/** `@font-face` des polices auto-hébergées (servies depuis /public/fonts).
 *  À injecter dans le document isolé du PDF. Inutile côté email : aucun
 *  client mail ne charge de webfont.
 *  `font-display:block` et non `swap` : l'export attend `document.fonts.ready`
 *  puis capture une seule fois — un rendu en police système ne se rattrape pas. */
export const FONT_FACES = `
@font-face{font-family:'Outfit';src:url('/fonts/outfit-latin.woff2') format('woff2');font-weight:100 900;font-style:normal;font-display:block;}
@font-face{font-family:'Outfit';src:url('/fonts/outfit-latin-ext.woff2') format('woff2');font-weight:100 900;font-style:normal;font-display:block;unicode-range:U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+1E00-1E9F,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113;}
@font-face{font-family:'Acorn';src:url('/fonts/acorn-regular.woff2') format('woff2');font-weight:400;font-style:normal;font-display:block;}`;

/* ── Signes graphiques ───────────────────────────────────────────── */

/** Coche Visaly, peinte dans la couleur demandée. Le fichier public n'existe
 *  qu'en blanc, et html2canvas ne charge pas de façon fiable un `<img>`
 *  externe : on inline donc les deux tracés. */
export function visalyMark(color: string, size = 34): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 181.42 179.6" width="${size}" height="${size}" aria-hidden="true"><path fill="${color}" d="M90.61,174.89c-2.27,0-4.63-.8-7.2-2.46-.54-.34-1.08-.7-1.62-1.06-3.2-2.1-6.83-4.48-10.84-5.76-2.02-.64-4.24-.94-7.01-.94-1.48,0-2.97.08-4.4.16-1.42.08-2.76.16-4.08.16-5.32,0-9.26-1.26-12.46-7.54-6.53-12.81-7.09-13.37-19.96-19.96l-.3-.15c-7.51-3.85-7.27-8.93-6.92-15.96.2-4,.4-8.13-1-12.25-1.14-3.34-2.96-6.19-4.72-8.95-3.86-6.04-6.65-10.4-1.93-17.77.34-.54.7-1.08,1.05-1.62,2.24-3.43,4.77-7.31,5.94-11.64,1.01-3.77.82-7.47.63-11.04-.36-6.91-.62-11.9,7-15.91l1.5-.79c11.7-6.15,12.04-6.48,18.04-17.96l.91-1.73c3.11-5.93,6.67-7.05,12.17-7.05,1.16,0,2.35.05,3.6.1,1.33.06,2.7.11,4.1.11,2.44,0,4.47-.18,6.39-.56,4.38-.87,8.29-3.49,11.74-5.8.55-.37,1.09-.73,1.61-1.07,2.81-1.84,5.36-2.73,7.79-2.73,3.76,0,7.11,2.17,10.66,4.47,3.1,2.01,6.31,4.08,10.14,5,2.04.49,4.22.71,6.86.71,1.42,0,2.81-.06,4.16-.12,1.29-.06,2.5-.11,3.69-.11,5.46,0,9.25,1.26,12.48,7.71,6.11,12.2,7.37,13.16,16.9,18.22,1.14.61,2.42,1.28,3.86,2.07,6.65,3.61,6.52,7.87,6.3,14.93-.1,3.1-.2,6.61.36,10.4.6,4.07,2.86,7.54,5.04,10.9,4.03,6.2,7.21,11.09,1.72,19.18l-.53.78c-2.3,3.38-5.17,7.58-6.08,12.06-.79,3.91-.64,7.64-.51,10.93.28,6.94.46,11.53-6.87,15.48-1.02.55-1.96,1.05-2.83,1.51-10.27,5.48-10.72,5.91-15.76,15.34-.64,1.19-1.36,2.53-2.2,4.07-3.06,5.6-6.61,6.69-11.72,6.69-1.21,0-2.46-.06-3.78-.12-1.38-.07-2.81-.14-4.27-.14-2.61,0-4.77.23-6.78.72-3.83.93-7.07,3.01-10.2,5.03-3.58,2.3-6.95,4.48-10.66,4.48ZM63.67,155.36c7.94,0,15.38,2.62,22.12,7.8,1.45,1.11,3.01,1.68,4.64,1.68h0c2.74,0,5.13-1.54,7.9-3.33,4.29-2.77,9.63-6.21,18.94-6.21,1.83,0,3.76.14,5.74.41.48.07.96.1,1.42.1,3.33,0,5.97-1.8,7.24-4.94,3.8-9.42,10.46-16.15,19.77-19.99,5.66-2.33,5.41-7.2,5.18-11.91-.28-5.68-.67-13.47,7.1-23.68,2.71-3.57,2.73-7.45.05-10.94-6.05-7.88-8.42-16.99-7.04-27.09.75-5.49-3.42-7.62-7.45-9.69-5.27-2.7-12.49-6.4-17.59-18.83-1.8-4.38-5.15-5.03-6.99-5.03-.46,0-.94.04-1.42.11-1.82.27-3.65.41-5.45.41-7.81,0-15.37-2.65-21.88-7.67-1.68-1.3-3.35-1.93-5.11-1.93s-3.59.69-5.48,2.1c-6.65,4.99-14,7.51-21.84,7.51-1.89,0-3.82-.15-5.75-.45-.39-.06-.77-.09-1.14-.09-4.26,0-6.06,3.54-7.96,7.28-2.75,5.41-6.51,12.82-19.09,17.92-5.12,2.08-4.9,6.81-4.67,11.38.29,5.91.68,14.01-7.39,24.36-2.63,3.37-2.62,6.94.03,10.32,8.01,10.23,7.64,18.35,7.36,24.28-.21,4.6-.44,9.36,4.69,11.43,12.41,5.01,16.21,12.36,18.98,17.73,1.99,3.85,3.86,7.48,8.31,7.48.39,0,.8-.03,1.21-.09,1.87-.28,3.74-.43,5.57-.43Z"/><path fill="${color}" d="M81.07,114.47c-.6,0-1.72-.12-2.53-.93l-21.24-21.11c-2.02-2.01-2.17-4.86-.34-6.78.82-.86,2.09-1.38,3.38-1.38.84,0,2.06.22,3.1,1.25l15.8,15.7c.55.55,1.28.82,2,.82s1.45-.28,2-.83l29.93-29.94c1.03-1.03,2.29-1.6,3.54-1.6,1,0,1.96.36,2.8,1.03,1.03.83,1.78,2.31,1.85,3.67.06,1.05-.27,1.93-.96,2.63l-36.26,36.31c-.7.7-1.91,1.15-3.1,1.15Z"/></svg>`;
}

/** Silhouette de typologie — le vocabulaire de formes de l'écran. */
export function typologyShapeSvg(
  type: AnalysisType | string | null | undefined,
  color: string,
  size: number,
  shape?: ShapeName,
): string {
  const spec = SHAPES[shape ?? shapeFor(type)];
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="${size}" height="${size}" aria-hidden="true"><polygon points="${spec.points}" fill="${color}" stroke="${color}" stroke-width="${spec.stroke}" stroke-linejoin="round"/></svg>`;
}

const RING_SEGMENTS = [
  { fill: '#f5dd6d', pale: '#fbefc0' }, // jaune
  { fill: '#f2a0ce', pale: '#fbe2f0' }, // rose
  { fill: '#b7cdf0', pale: '#e3ecfa' }, // bleu
  { fill: '#a9be7e', pale: '#e8eed6' }, // kaki
];

/** Anneau de score segmenté — version statique de `ui/ScoreRing`. Le
 *  nuancier complet reste visible : les arcs non atteints restent pâles. */
export function scoreRingSvg(score: number, size = 152, thickness = 15): string {
  const R = 78;
  const CIRC = 2 * Math.PI * R;
  const GAP = 16;
  const SPAN = CIRC / RING_SEGMENTS.length;
  const LEN = SPAN - GAP;
  const s = Math.max(0, Math.min(100, score));

  const arcs = RING_SEGMENTS.map((seg, i) => {
    const start = (i * SPAN + GAP / 2).toFixed(2);
    const ratio = Math.max(0, Math.min(1, (s - i * 25) / 25));
    const pale = `<circle cx="100" cy="100" r="${R}" fill="none" stroke-linecap="round" stroke="${seg.pale}" stroke-width="${thickness}" stroke-dasharray="${LEN.toFixed(2)} ${(CIRC - LEN).toFixed(2)}" stroke-dashoffset="-${start}"/>`;
    if (ratio <= 0) return pale;
    const on = LEN * ratio;
    return pale + `<circle cx="100" cy="100" r="${R}" fill="none" stroke-linecap="round" stroke="${seg.fill}" stroke-width="${thickness}" stroke-dasharray="${on.toFixed(2)} ${(CIRC - on).toFixed(2)}" stroke-dashoffset="-${start}"/>`;
  }).join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 200 200" aria-hidden="true"><g transform="rotate(-90 100 100)">${arcs}</g></svg>`;
}

/** Le même motif, mis à plat en jauge : c'est la version qui survit aux
 *  clients mail (aucun SVG, que des cellules de table et des aplats). */
export function scoreBarHtml(score: number, width = 264): string {
  const s = Math.max(0, Math.min(100, score));
  const seg = Math.round((width - 24) / RING_SEGMENTS.length);
  const cells = RING_SEGMENTS.map((sg, i) => {
    const ratio = Math.max(0, Math.min(1, (s - i * 25) / 25));
    const on = Math.round(seg * ratio);
    const off = seg - on;
    return `<td style="padding:0 3px;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;"><tr>
        ${on > 0 ? `<td width="${on}" height="9" style="background:${sg.fill};border-radius:5px;font-size:0;line-height:0;">&nbsp;</td>` : ''}
        ${off > 0 ? `<td width="${off}" height="9" style="background:${sg.pale};border-radius:5px;font-size:0;line-height:0;">&nbsp;</td>` : ''}
      </tr></table>
    </td>`;
  }).join('');
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;margin:0 auto;"><tr>${cells}</tr></table>`;
}

/* ── Icônes linéaires ────────────────────────────────────────────────
   Trait 1.7, boîte 24 — le jeu du site (`client/site/ui.tsx`). */

const PATHS: Record<string, string> = {
  check: 'm5 12.5 4.5 4.5L19 7.5',
  x: 'M6 6l12 12M18 6 6 18',
  bolt: 'M3.75 13.5 14.25 1.5 12 10.5h8.25L9.75 22.5 12 13.5H3.75Z',
  shield: 'M9 12.75 11.25 15 15 9.75M12 3.5l7 2.5v5.5c0 4.2-2.8 7.4-7 9-4.2-1.6-7-4.8-7-9V6l7-2.5Z',
  doc: 'M19.5 14.25v-2.6a3.4 3.4 0 0 0-3.4-3.4h-1.5a1.1 1.1 0 0 1-1.1-1.1v-1.5a3.4 3.4 0 0 0-3.4-3.4H8.25m2.25 0H5.6c-.6 0-1.1.5-1.1 1.1v17.3c0 .6.5 1.1 1.1 1.1h12.8c.6 0 1.1-.5 1.1-1.1V11.25a9 9 0 0 0-9-9Z',
  scale: 'M12 3v17.25M5.25 20.25h13.5M18.75 4.97 21.37 15.7M18.75 4.97 16.13 15.7M5.25 4.97 7.87 15.7M5.25 4.97 2.63 15.7M18.75 4.97A48.4 48.4 0 0 0 12 4.5c-2.29 0-4.55.16-6.75.47',
  flag: 'M4.5 21V4.5m0 0h11l-2 4 2 4h-11',
  sparkles: 'M9.8 15.9 9 18.75l-.8-2.85a4.5 4.5 0 0 0-3.1-3.09L2.25 12l2.85-.81a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.81 2.85a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.85.81a4.5 4.5 0 0 0-3.09 3.09Z',
  dash: 'M5 12h14',
};

export function icon(name: string, color: string, size = 14, stroke = 1.7): string {
  const d = PATHS[name] ?? PATHS.dash;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${color}" stroke-width="${stroke}" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;
}
